/* ============================================================
   BNE STUDIO — Sync (Midnight Blueprint)
   Recreated from the design handoff:
   "BNE Studio - Sync.html". Renders inside NightLayout
   (shared nav/footer + motion engine + scoped design system).
   Cross-links map to app routes. The page lives at /sync.
   ============================================================ */
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import NightLayout from "../components/night/NightLayout";

// arrow used by the primary CTAs
const Arrow = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SYNC_URL = "https://one.bne.studio";

export const SyncPage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = "BNE Sync — Keep the creative work in one place";
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <NightLayout description="Every file, take, and approval for your song in one place — so nothing ever gets lost." active="sync">
      {/* HERO */}
      <header className="phero" style={{ "--c": "var(--p-sync)" }}>
        <div className="wrap">
          <div className="phero-grid">
            <div className="reveal">
              <div className="ico-badge">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 6a2 2 0 012-2h4l2 2.5h6a2 2 0 012 2V18a2 2 0 01-2 2H6a2 2 0 01-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="eyebrow" style={{ marginBottom: "16px" }}>Create</span>
              <h1 className="grad-text">Your studio, in sync.</h1>
              <p className="lead">Every file, take, and approval for your song in one place — so nothing ever gets lost.</p>
              <div className="ctas">
                <a className="btn btn-primary btn-lg btn-arrow" href={SYNC_URL}>
                  Open Sync <Arrow />
                </a>
                <span className="domain">one.bne.studio</span>
              </div>
            </div>

            <div className="reveal" data-d="1">
              <div className="big-mock">
                <div className="bm-bar">
                  <i />
                  <i />
                  <i />
                  <span className="t">one.bne.studio / afterglow-ep</span>
                  <span className="pill">Review</span>
                </div>
                <div className="bm-body">
                  <div className="row">
                    <span style={{ fontFamily: "var(--mono)", fontSize: "12px", color: "var(--heading)", width: "72px" }}>01 Drift</span>
                    <span className="wave" style={{ "--wh": "24px", color: "var(--p-sync)", flex: 1 }}>
                      <i style={{ height: "7px" }} />
                      <i style={{ height: "16px" }} />
                      <i style={{ height: "10px" }} />
                      <i style={{ height: "20px" }} />
                      <i style={{ height: "8px" }} />
                      <i style={{ height: "15px" }} />
                      <i style={{ height: "6px" }} />
                      <i style={{ height: "18px" }} />
                      <i style={{ height: "12px" }} />
                      <i style={{ height: "7px" }} />
                      <i style={{ height: "14px" }} />
                      <i style={{ height: "9px" }} />
                      <i style={{ height: "17px" }} />
                      <i style={{ height: "11px" }} />
                    </span>
                    <span className="chip-status st-live">OK</span>
                  </div>
                  <div className="row">
                    <span style={{ fontFamily: "var(--mono)", fontSize: "12px", color: "var(--heading)", width: "72px" }}>02 Halo</span>
                    <span className="wave" style={{ "--wh": "24px", color: "var(--p-sync)", flex: 1 }}>
                      <i style={{ height: "10px" }} />
                      <i style={{ height: "18px" }} />
                      <i style={{ height: "7px" }} />
                      <i style={{ height: "14px" }} />
                      <i style={{ height: "20px" }} />
                      <i style={{ height: "9px" }} />
                      <i style={{ height: "16px" }} />
                      <i style={{ height: "11px" }} />
                      <i style={{ height: "6px" }} />
                      <i style={{ height: "15px" }} />
                      <i style={{ height: "8px" }} />
                      <i style={{ height: "13px" }} />
                      <i style={{ height: "17px" }} />
                      <i style={{ height: "10px" }} />
                    </span>
                    <span className="chip-status st-q">Review</span>
                  </div>
                  <div style={{ display: "flex", gap: "10px", padding: "14px", borderRadius: "11px", boxShadow: "var(--hair-soft)", background: "rgba(186,215,247,.015)", margin: "10px 0" }}>
                    <span style={{ width: "30px", height: "30px", borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", color: "#fff", fontSize: "11px", fontWeight: 700, background: "var(--p-sync)" }}>JM</span>
                    <div>
                      <b style={{ fontSize: "13px", color: "var(--heading)" }}>Jordan M. <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--fog)", fontWeight: 400 }}>· 1:24</span></b>
                      <p style={{ fontSize: "13px", color: "var(--pebble)", marginTop: "3px", lineHeight: 1.4 }}>Vocal up 1dB in the second chorus — otherwise the master's ready to go.</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <span className="chip" style={{ color: "var(--ok)" }}>✓ Master approved</span>
                    <span className="chip">3 collaborators</span>
                    <span className="chip">v4 · current</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* OUTCOMES */}
      <section className="section tight" style={{ "--c": "var(--p-sync)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">What it helps you do</span>
            <h2>The whole project, moving together.</h2>
          </div>
          <div className="outcomes">
            <div className="outcome reveal" data-d="1">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 2 2 7l10 5 10-5-10-5ZM2 12l10 5 10-5M2 17l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Organize stems &amp; assets</h4>
              <p>Keep tracks, stems, artwork, and project files in one place the whole team can reach.</p>
            </div>
            <div className="outcome reveal" data-d="2">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M21 11.5a8.4 8.4 0 0 1-12.8 7.2L3 21l2.3-5.2A8.4 8.4 0 1 1 21 11.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Review in context</h4>
              <p>Leave timestamped notes on a mix and resolve them without a single email thread.</p>
            </div>
            <div className="outcome reveal" data-d="3">
              <div className="oi">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h4>Approve &amp; hand off</h4>
              <p>Lock the final master, then send it straight into Distribution or Vision.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="section" style={{ "--c": "var(--p-sync)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">How it works</span>
            <h2>From rough idea to approved master.</h2>
          </div>
          <div className="work-split">
            <div className="vsteps reveal">
              <div className="vstep">
                <div className="n">1</div>
                <div>
                  <h4>Start a project</h4>
                  <p>Create a space for the song and invite collaborators with the right access.</p>
                </div>
              </div>
              <div className="vstep">
                <div className="n">2</div>
                <div>
                  <h4>Upload versions</h4>
                  <p>Add stems and mixes — every version kept in order, nothing overwritten.</p>
                </div>
              </div>
              <div className="vstep">
                <div className="n">3</div>
                <div>
                  <h4>Review &amp; resolve</h4>
                  <p>Drop timestamped notes, reply in context, and mark feedback as done.</p>
                </div>
              </div>
              <div className="vstep">
                <div className="n">4</div>
                <div>
                  <h4>Approve the master</h4>
                  <p>Sign off the final, then push it into the next step of the rollout.</p>
                </div>
              </div>
            </div>
            <div className="mock reveal" data-d="1">
              <div className="mock-bar">
                <i />
                <i />
                <i />
                <span className="t">one.bne.studio / neon-hours</span>
                <span className="pill">Project</span>
              </div>
              <div className="mock-body">
                <div className="row">
                  <span className="m">
                    <b>Neon Hours — master v4</b>
                    <span>uploaded by Ava R.</span>
                  </span>
                  <span className="chip-status st-q">In review</span>
                </div>
                <div style={{ display: "flex", gap: "10px", padding: "12px", borderRadius: "11px", boxShadow: "var(--hair-soft)", background: "rgba(186,215,247,.015)", margin: "8px 0" }}>
                  <span style={{ width: "28px", height: "28px", borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", color: "#fff", fontSize: "11px", fontWeight: 700, background: "var(--p-sync)" }}>JM</span>
                  <div>
                    <b style={{ fontSize: "12.5px", color: "var(--heading)" }}>Jordan M. · 1:24</b>
                    <p style={{ fontSize: "12.5px", color: "var(--pebble)", marginTop: "2px", lineHeight: 1.4 }}>Vocal up 1dB in the second chorus — otherwise ready to approve.</p>
                  </div>
                </div>
                <div className="row">
                  <span className="m">
                    <b>Master approved</b>
                    <span>signed off by 3 of 3</span>
                  </span>
                  <span className="chip-status st-live">Done</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="section tight" style={{ "--c": "var(--p-sync)" }}>
        <div className="wrap">
          <div className="proof">
            <div className="pc reveal" data-d="1">
              <div className="lab">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 8v4l3 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Version history
              </div>
              <h4>Never lose a take.</h4>
              <p>Every upload is kept in order, so you can compare versions and roll back without hunting through folders.</p>
              <div style={{ marginTop: "4px" }}>
                <div className="row">
                  <span className="m">
                    <b>v4 — final master</b>
                    <span>approved · 2h ago</span>
                  </span>
                  <span className="chip-status st-live">Current</span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>v3 — chorus fix</b>
                    <span>Jordan M. · yesterday</span>
                  </span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--fog)" }}>archived</span>
                </div>
                <div className="row">
                  <span className="m">
                    <b>v2 — rough mix</b>
                    <span>3 days ago</span>
                  </span>
                  <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--fog)" }}>archived</span>
                </div>
              </div>
            </div>

            <div className="pc reveal" data-d="2">
              <div className="lab">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Approvals
              </div>
              <h4>Sign-off that sticks.</h4>
              <p>Collect approvals from everyone who needs to weigh in, with a clear record of who signed off and when.</p>
              <div style={{ marginTop: "4px" }}>
                <div className="row">
                  <span style={{ width: "30px", height: "30px", borderRadius: "50%", display: "grid", placeItems: "center", color: "#fff", fontSize: "11px", fontWeight: 700, background: "var(--p-sync)" }}>JM</span>
                  <span className="m">
                    <b>Jordan M.</b>
                    <span>producer</span>
                  </span>
                  <span className="chip-status st-live">Approved</span>
                </div>
                <div className="row">
                  <span style={{ width: "30px", height: "30px", borderRadius: "50%", display: "grid", placeItems: "center", color: "#fff", fontSize: "11px", fontWeight: 700, background: "var(--p-live)" }}>AR</span>
                  <span className="m">
                    <b>Ava R.</b>
                    <span>artist</span>
                  </span>
                  <span className="chip-status st-live">Approved</span>
                </div>
                <div className="row">
                  <span style={{ width: "30px", height: "30px", borderRadius: "50%", display: "grid", placeItems: "center", color: "#fff", fontSize: "11px", fontWeight: 700, background: "var(--p-contracts)" }}>DL</span>
                  <span className="m">
                    <b>Dre L.</b>
                    <span>label A&amp;R</span>
                  </span>
                  <span className="chip-status st-q">Pending</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="section" style={{ "--c": "var(--p-sync)" }}>
        <div className="wrap">
          <div className="sec-head reveal">
            <span className="eyebrow">Connected to the rest of BNE</span>
            <h2>Where the work goes next.</h2>
          </div>
          <div className="eco-grid">
            <Link className="eco reveal" data-d="1" style={{ "--c": "var(--p-dist)" }} to="/distribute">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Distribution</b>
              <p>Send the approved master into a release.</p>
            </Link>
            <Link className="eco reveal" data-d="2" style={{ "--c": "var(--p-vision)" }} to="/vision">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M20.2 6 3 11l-.9-2.4c-.3-1.1.3-2.2 1.3-2.5l13.5-4c1.1-.3 2.2.3 2.5 1.3ZM3 11h18v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Vision</b>
              <p>Turn approved assets into rollout visuals.</p>
            </Link>
            <Link className="eco reveal" data-d="3" style={{ "--c": "var(--p-contracts)" }} to="/contracts">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M14 3v4a1 1 0 0 0 1 1h4M18 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8l6 6v3M8 18c1.4-2 2.8-2 4 0s2.6 2 4 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>Contracts</b>
              <p>Document splits with the right collaborators.</p>
            </Link>
            <Link className="eco reveal" data-d="4" style={{ "--c": "var(--p-live)" }} to="/live">
              <div className="ei">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M4 8a2 2 0 012-2h12a2 2 0 012 2 2 2 0 000 4 2 2 0 000 4 2 2 0 01-2 2H6a2 2 0 01-2-2 2 2 0 000-4 2 2 0 000-4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <b>BNE Live</b>
              <p>Take the finished song to the stage.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section tight">
        <div className="wrap">
          <div className="cta-band reveal-scale">
            <div className="glow" />
            <span className="eyebrow center" style={{ justifyContent: "center", marginBottom: "20px" }}>
              Create · Sync
            </span>
            <h2 className="grad-text">Bring the team into one space.</h2>
            <p>Stems, notes, approvals, and versions — together, and ready for release.</p>
            <div className="row" style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
              <a className="btn btn-primary btn-lg btn-arrow" href={SYNC_URL}>
                Open Sync <Arrow />
              </a>
              <Link className="btn btn-ghost btn-lg" to="/home">
                Explore the Ecosystem
              </Link>
            </div>
          </div>
        </div>
      </section>
    </NightLayout>
  );
};

export default SyncPage;
